import React, { useEffect, useMemo, useState } from 'react';
import { addDoc, collection, serverTimestamp } from 'firebase/firestore';
import { appId, db } from './firebase-config';

const PLAN_INTEREST_OPTIONS = {
  base: 'Plan Base',
  intermedio: 'Plan Intermedio',
  avanzado: 'Plan Avanzado',
};

const buildInitialForm = (requestedPlan = 'base') => ({
  companyName: '',
  contactName: '',
  email: '',
  phone: '',
  operationSize: '',
  interest: PLAN_INTEREST_OPTIONS[requestedPlan] || 'Plan Base',
  message: '',
});

const DemoRequestPage = ({ onBackHome, onGoToLogin, requestedPlan = 'base' }) => {
  const [form, setForm] = useState(() => buildInitialForm(requestedPlan));
  const [status, setStatus] = useState({ type: '', text: '' });
  const [submitting, setSubmitting] = useState(false);
  const selectedPlanLabel = PLAN_INTEREST_OPTIONS[requestedPlan] || 'Plan Base';

  useEffect(() => {
    setForm(buildInitialForm(requestedPlan));
    setStatus({ type: '', text: '' });
  }, [requestedPlan]);

  const canSubmit = useMemo(() => {
    return form.companyName.trim() && form.contactName.trim() && form.email.trim();
  }, [form]);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!canSubmit || submitting) return;

    setSubmitting(true);
    setStatus({ type: '', text: '' });

    try {
      await addDoc(collection(db, `artifacts/${appId}/commercialLeads`), {
        ...form,
        requestedPlan,
        requestedPlanKey: requestedPlan,
        requestedPlanTitle: selectedPlanLabel,
        source: 'public-plan-request',
        status: 'new',
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });

      setStatus({
        type: 'success',
        text: 'Solicitud registrada. Vamos a revisar tus datos para continuar con el plan elegido.',
      });
      setForm(buildInitialForm(requestedPlan));
    } catch (error) {
      console.error('Error registrando solicitud comercial:', error);
      setStatus({
        type: 'error',
        text: 'No pudimos registrar la solicitud en este momento. Volvé a intentarlo más tarde.',
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white px-4 py-8 text-slate-900 sm:px-6 lg:px-8">
      <div className="mx-auto max-w-5xl">
        <div className="flex flex-wrap items-center justify-between gap-4 border-b border-slate-200 pb-6">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.24em] text-cyan-700">G-CENTRA</p>
            <h1 className="mt-2 text-3xl font-black tracking-tight">Solicitar plan</h1>
            <p className="mt-3 max-w-2xl text-sm leading-6 text-slate-600">
              Esta pantalla no crea un trial. Acá registramos la solicitud comercial para activar el plan que querés sumar a tu operación.
            </p>
            <div className="mt-4 inline-flex items-center gap-2 rounded-full border border-cyan-200 bg-cyan-50 px-3 py-2 text-xs font-black uppercase tracking-[0.18em] text-cyan-800">
              Plan solicitado: {selectedPlanLabel}
            </div>
          </div>

          <div className="flex flex-wrap gap-3">
            <button
              type="button"
              onClick={onBackHome}
              className="rounded-2xl border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-700 transition hover:bg-slate-50"
            >
              Volver al inicio
            </button>
            <button
              type="button"
              onClick={onGoToLogin}
              className="rounded-2xl bg-cyan-600 px-4 py-2 text-sm font-bold text-white transition hover:bg-cyan-700"
            >
              Ingresar
            </button>
          </div>
        </div>

        <div className="mt-8 grid gap-6 lg:grid-cols-[0.9fr_1.1fr]">
          <aside className="rounded-3xl border border-slate-200 bg-slate-50 p-6">
            <p className="text-xs font-semibold uppercase tracking-[0.22em] text-cyan-700">Qué confirmamos</p>
            <ul className="mt-5 space-y-3 text-sm leading-6 text-slate-600">
              <li>• qué plan querés activar, distinto del trial gratuito</li>
              <li>• qué plan están evaluando para la operación</li>
              <li>• cómo organizan hoy sus rutas, unidades o stock</li>
              <li>• qué nivel de seguimiento y trazabilidad necesitan</li>
              <li>• cuál es el mejor punto de activación para empezar</li>
            </ul>
          </aside>

          <form onSubmit={handleSubmit} className="rounded-3xl border border-slate-200 bg-white p-6">
            <div className="grid gap-4 sm:grid-cols-2">
              <label className="block text-sm text-slate-700">
                Empresa
                <input name="companyName" value={form.companyName} onChange={handleChange} className="mt-2 w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-slate-900 outline-none transition focus:border-cyan-500 focus:ring-2 focus:ring-cyan-100" />
              </label>

              <label className="block text-sm text-slate-700">
                Responsable
                <input name="contactName" value={form.contactName} onChange={handleChange} className="mt-2 w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-slate-900 outline-none transition focus:border-cyan-500 focus:ring-2 focus:ring-cyan-100" />
              </label>

              <label className="block text-sm text-slate-700">
                Email
                <input type="email" name="email" value={form.email} onChange={handleChange} className="mt-2 w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-slate-900 outline-none transition focus:border-cyan-500 focus:ring-2 focus:ring-cyan-100" />
              </label>

              <label className="block text-sm text-slate-700">
                Teléfono
                <input name="phone" value={form.phone} onChange={handleChange} className="mt-2 w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-slate-900 outline-none transition focus:border-cyan-500 focus:ring-2 focus:ring-cyan-100" />
              </label>

              <label className="block text-sm text-slate-700">
                Tamaño de la operación
                <input name="operationSize" value={form.operationSize} onChange={handleChange} placeholder="Ej. 12 unidades / 3 bases" className="mt-2 w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-slate-900 outline-none transition focus:border-cyan-500 focus:ring-2 focus:ring-cyan-100" />
              </label>

              <label className="block text-sm text-slate-700">
                Plan de interés
                <select name="interest" value={form.interest} onChange={handleChange} className="mt-2 w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-slate-900 outline-none transition focus:border-cyan-500 focus:ring-2 focus:ring-cyan-100">
                  <option>Plan Base</option>
                  <option>Plan Intermedio</option>
                  <option>Plan Avanzado</option>
                </select>
              </label>
            </div>

            <label className="mt-4 block text-sm text-slate-700">
              Mensaje
              <textarea name="message" value={form.message} onChange={handleChange} rows={5} className="mt-2 w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-slate-900 outline-none transition focus:border-cyan-500 focus:ring-2 focus:ring-cyan-100" placeholder="Contanos brevemente qué te gustaría mejorar en tu operación." />
            </label>

            {status.text ? (
              <div className={`mt-4 rounded-2xl border px-4 py-3 text-sm font-medium ${status.type === 'success' ? 'border-emerald-200 bg-emerald-50 text-emerald-700' : 'border-red-200 bg-red-50 text-red-700'}`}>
                {status.text}
              </div>
            ) : null}

            <div className="mt-6 flex flex-wrap gap-3">
              <button type="submit" disabled={!canSubmit || submitting} className="rounded-2xl bg-cyan-600 px-5 py-3 text-sm font-bold text-white transition hover:bg-cyan-700 disabled:cursor-not-allowed disabled:opacity-60">
                {submitting ? 'Enviando...' : 'Enviar solicitud'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default DemoRequestPage;
